/**
 * B332 — a buyer's refusal is about ONE step of the §7 walk.
 *
 * The storefront draws a single refusal line above the whole walk. Before
 * this module that line was a bare string with no provenance: nothing
 * recorded which step it was about, so nothing could retire it, and the
 * only thing that ever cleared it was a successful payment. A promo code
 * turned down on the cart therefore followed the buyer onto the payment
 * screen, where a stranger was asked for their card underneath a red
 * sentence about a step that had since worked.
 *
 * Pure, and deliberately tiny. The screen a refusal belongs to is the one
 * the buyer LANDS on, not the one they were leaving — `checkout` refuses
 * from CHECKOUT_IN_FLIGHT and dispatches CHECKOUT_REFUSED, which puts the
 * buyer back on the cart, and that is where its sentence must speak. So
 * the step is named by the caller at the point of refusal rather than read
 * off the flow, which would be the screen already left behind.
 */

import type { ScreenKind } from "../ui/screens.js";

/** A buyer-facing refusal, and the step of the walk it is about. */
export interface BuyerNotice {
  /** Buyer words, always from `refusals.ts` — the container authors none. */
  readonly text: string;
  /** The screen on which this sentence is true. */
  readonly screen: ScreenKind;
}

/**
 * The sentence to draw, if any. On any screen but its own a refusal is
 * history, and drawing history tells the buyer that something failed on a
 * step that has since worked.
 */
export function noticeOnScreen(notice: BuyerNotice | undefined, screen: ScreenKind): string | undefined {
  return notice !== undefined && notice.screen === screen ? notice.text : undefined;
}
